import { Box, Typography, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { GET } from "../../../Utilities/BaseService";
import BaseTable from "../../Table/BaseTable";
import { BillFilter } from "../Bill/BillFilter";
import {
    currencyFormatter,
    numberFormatter,
} from "../Bill/Cells/Last12Summary";
import { monthFormatter } from "../Bill/SingleBill";

const fetchBills = async (filter: any) => {
    return await GET("/reading/bill/generate", {
        ...filter,
        ignoreCompleted: true,
    });
};

const BillReport = () => {
    const [pagination, setPagination] = useState({
        page: 0,
        count: 0,
        limit: 500,
    });
    const [feedback, setFeedback] = useState("");
    const [count, setCount] = useState(0);

    const [filter, setFilter] = useState<any>({
        district: "all",
        village: "all",
        hamlet: "all",
        powerstation: "all",
        id: "",
        month: "",
        year: "",
        meterNo: [],
        search: "",
    });
    const [showBills, setShowBills] = useState(false);
    const theme = useTheme();


    const { data: billsResponse, isFetching: isLoading } = useQuery(
        ["bills", showBills, filter, pagination.page, pagination.limit, count],
        () =>
            fetchBills({
                ...Object.fromEntries(
                    Object.entries(filter).filter((x) => x[1] && x[1] !== "all")
                ),
                page: pagination.page + 1,
                limit: pagination.limit,
                status: "active",
                search: undefined,
                meterNo: filter.search ? [filter.search] : undefined,
            }),
        {
            enabled: showBills,
            onSuccess(res) {
                console.log("Fetched bills:", res.data.rows);
                setPagination((prev) => ({ ...prev, count: res.data.count }));
            },
        }
    );

    const createSummary = (cols: string[]) => {
        const summaryRow = ["Total"];
        if (
            !billsResponse?.data?.rows ||
            !Array.isArray(billsResponse?.data?.rows)
        )
            return summaryRow;

        let billAmount = 0;
        let lineRent = 0;
        let unitsAmount = 0;
        let additonalCharges = 0;
        let surchargeAmount = 0;
        let arrears = 0;
        let connectionChargesAmount = 0;
        let payableWithinDueDate = 0;
        let payableAfterDueDate = 0;
        let unitsGenerated = 0;
        let amount = 0;
        let consumedUnits = 0;

        for (const row of billsResponse?.data.rows) {
            amount += row.totalAmount + row.arrears - row.ledgerBalance;
            billAmount += row.lineRent + row.unitsAmount;
            lineRent += row.lineRent;
            consumedUnits += row.consumedUnits;
            unitsAmount += row.unitsAmount;
            additonalCharges += (row?.additionalCharges ?? []).reduce(
                (prev: number, curr: any) =>
                    currencyFormatter.format(prev + (curr?.amount || 0)),
                0
            );
            surchargeAmount += row.applicableSurcharge;
            arrears += row.arrears;
            connectionChargesAmount += row.connectionChargesAmount;
            payableWithinDueDate += amount < 0 ? amount : amount;
            payableAfterDueDate +=
                amount < 0 ? amount : amount + row.applicableSurcharge;
            unitsGenerated +=
                row.wrongReadingFlag == false ? 0 : row.consumedUnits;
        }

        for (const col of cols) {
            switch (col) {
                case "payableWithinDueDate":
                    summaryRow.push(
                        currencyFormatter.format(payableWithinDueDate)
                    );
                    break;

                case "unitsConsumed":
                    summaryRow.push(numberFormatter.format(consumedUnits));
                    break;

                case "billAmount":
                    summaryRow.push(currencyFormatter.format(billAmount));
                    break;

                case "payableAfterDueDate":
                    summaryRow.push(
                        currencyFormatter.format(payableAfterDueDate)
                    );
                    break;

                case "lineRent":
                    summaryRow.push(currencyFormatter.format(lineRent));
                    break;

                case "unitsAmount":
                    summaryRow.push(currencyFormatter.format(unitsAmount));
                    break;

                case "additonalCharges":
                    summaryRow.push(currencyFormatter.format(additonalCharges));
                    break;

                case "surchargeAmount":
                    summaryRow.push(currencyFormatter.format(surchargeAmount));
                    break;

                case "arrears":
                    summaryRow.push(currencyFormatter.format(arrears));
                    break;

                case "connectionChargesAmount":
                    summaryRow.push(
                        currencyFormatter.format(connectionChargesAmount)
                    );
                    break;

                default:
                    summaryRow.push("");
            }
        }
        return summaryRow;
    };

    return (
        <Box>
            <BillFilter
                filter={filter}
                setFilter={setFilter}
                isLoading={false}
                setShowBills={setShowBills}
            />

<Box
    sx={{
        mt: 1.5,
        mb: 3,
        p: 1.5,
        borderLeft: `4px solid ${theme.palette.secondary.main}`,
        bgcolor: "grey.50",
        borderRadius: 1,
    }}
>
    <Typography variant="body2">
        This report contains consumer billing information, including Meter
        Number, Customer Name, Line Rent, Units Consumed, Units Amount,
        Bill Amount, Surcharge, Additional Charges, Arrears, Connection
        Charges, Payable Amount (Within Due Date and After Due Date),
        Billing Month, and Bill Status.
    </Typography>

    <Typography
        variant="body2"
        sx={{
            mt: 0.5,
            direction: "rtl",
            textAlign: "right",
        }}
    >
        اس رپورٹ میں صارف کے بل کی معلومات شامل ہیں، جن میں میٹر نمبر،
        صارف کا نام، لائن رینٹ، استعمال شدہ یونٹس، یونٹس کی رقم، بل کی
        رقم، سرچارج، اضافی چارجز، بقایا جات، کنکشن چارجز، مقررہ تاریخ
        تک قابلِ ادائیگی رقم، مقررہ تاریخ کے بعد قابلِ ادائیگی رقم،
        بلنگ ماہ اور بل کی حیثیت شامل ہیں۔
    </Typography>
</Box>

            <BaseTable
                headers={{
                    id: "Id",
                    meterNo: "MeterNo",
                    customerName: "Customer Name",
                    payableWithinDueDate: "Payable Within DueDate",
                    payableAfterDueDate: "Payable After DueDate",
                    lineRent: "Line Rent",
                    unitsAmount: "Units Amount",
                    billAmount: "Bill Amount",
                    billStatus: "Bill Status",
                    unitsConsumed: "Units Consumed",
                    arrears: "Arrears",
                    arrearsWithoutSurcharge: "arrearsWithoutSurcharge",
                    arrearsWithSurcharge: "arrearsWithSurcharge",
                    additonalCharges: "AdditonalCharges",
                    connectionChargesAmount: "ConnectionChargesAmount",
                    surchargeAmount: "Surcharge",
                    connectionCharges: "ConnectionCharges",
                    billingMonth: "billingMonth",
                    district: "District",
                    village: "Village",
                    hamlet: "Hamlet",
                    powerstation: "Powerstation",
                }}
                defaultSelectedHeaders={[
                    "Id",
                    "meterNo",
                    "customerName",
                    "lineRent",
                    "unitsAmount",
                    "unitsConsumed",
                    "billAmount",
                    "surchargeAmount",
                    "additonalCharges",
                    "arrears",
                    "connectionChargesAmount",
                    "payableWithinDueDate",
                    "payableAfterDueDate",
                    "billingMonth",
                    "billStatus",
                ]}
                data={
                    Array.isArray(billsResponse?.data?.rows)
                        ? billsResponse?.data?.rows.map(
                              (row: any, serialNo: number) => {
                                  const amount =
                                      row.totalAmount +
                                      row.arrears -
                                      row.ledgerBalance;

                                  const payableAfterDueDate =
                                      amount > 0
                                          ? `${currencyFormatter.format(amount)} CR (Not to be paid)`
                                          : currencyFormatter.format(
                                                amount +
                                                    (row.applicableSurcharge ??
                                                        0)
                                            );

                                  console.log(amount, payableAfterDueDate);

                                  return {
                                      serialNo: serialNo + 1,
                                      id: row.id,
                                      meterNo: row?.meter?.meterNo,

                                      billAmount: currencyFormatter.format(
                                          row.lineRent + row.unitsAmount
                                      ),

                                      lineRent: currencyFormatter.format(
                                          row.lineRent
                                      ),
                                      unitsAmount: currencyFormatter.format(
                                          row.unitsAmount
                                      ),
                                      unitsConsumed: row.consumedUnits,
                                      arrears: currencyFormatter.format(
                                          row?.arrears - row.ledgerBalance
                                      ),

                                      additonalCharges:
                                          currencyFormatter.format(
                                              (
                                                  row?.additionalCharges ?? []
                                              ).reduce(
                                                  (prev: number, curr: any) =>
                                                      prev +
                                                      (curr?.amount || 0),
                                                  0
                                              )
                                          ),

                                      connectionChargesAmount:
                                          currencyFormatter.format(
                                              row.connectionChargesAmount ?? 0
                                          ),

                                      surchargeAmount: currencyFormatter.format(
                                          row.applicableSurcharge ?? 0
                                      ),

                                      arrearsWithoutSurcharge:
                                          row.arrearsWithoutSurcharge,
                                      arrearsWithSurcharge: row.arrears,

                                      customerName:
                                          row?.meter?.property?.customer
                                              ?.name ?? "",

                                      village: row.meter?.village?.name ?? "",
                                      hamlet: row.meter?.hamlet?.name ?? "",
                                      district: row.meter?.district?.name ?? "",
                                      powerstation:
                                          row.meter?.powerstation?.name ?? "",

                                      billingMonth: monthFormatter.format(
                                          new Date(row.year, row.month, -1)
                                      ),

                                      billStatus:
                                          row.wrongReadingFlag === true
                                              ? "Incorrect"
                                              : "Correct",

                                      unitsGenerated:
                                          row.wrongReadingFlag === true
                                              ? 0
                                              : row.unitsGenerated,

                                      payableWithinDueDate:
                                          amount < 0
                                              ? `${currencyFormatter.format(amount)} CR (Not to be paid)`
                                              : currencyFormatter.format(
                                                    amount
                                                ),

                                      payableAfterDueDate:
                                          amount < 0
                                              ? `${currencyFormatter.format(amount)} CR (Not to be paid)`
                                              : currencyFormatter.format(
                                                    amount +
                                                        (row.applicableSurcharge ??
                                                            0)
                                                ),
                                  };
                              }
                          )
                        : []
                }
                summaryRow={createSummary}
                load
                delEndPoint="/region/district"
                feedback={feedback}
                setFeedback={setFeedback}
                rowsCount={pagination.count}
                pagination={pagination}
                setPagination={setPagination}
                permissions={{
                    edit: [""],
                    delete: ["DELETE READINdddd"],
                }}
                isLoading={isLoading}
                queryKey={["bills"]}
                reportName="BILLREPORT__List"
                reportDetails={{
                    district: filter.district ? filter.district : undefined,
                    powerstation: filter.powerstation
                        ? filter.powerstation
                        : undefined,
                    village: filter.village ? filter.village : undefined,
                    hamlet: filter.hamlet ? filter.hamlet : undefined,
                }}
            />
        </Box>
    );
};

export default BillReport;
